/**
 * @jsxRuntime classic
 * @jsx jsx
 */
import { jsx } from '@compiled/react';
import { FormattedMessage } from 'react-intl';

import Button from '@atlaskit/button/new';
import { cssMap } from '@atlaskit/css';
import Heading from '@atlaskit/heading';
import StatusErrorIcon from '@atlaskit/icon/core/status-error';
import { Box, Flex, Stack, Text } from '@atlaskit/primitives/compiled';
import { token } from '@atlaskit/tokens';

import messages from './messages';

const styles = cssMap({
	container: {
		display: 'flex',
		flexDirection: 'column',
		alignItems: 'center',
		justifyContent: 'center',
		flexGrow: 1,
		paddingTop: token('space.600'),
		paddingBottom: token('space.600'),
		paddingLeft: token('space.400'),
		paddingRight: token('space.400'),
		textAlign: 'center',
	},
	iconWrapper: {
		paddingBottom: token('space.200'),
	},
	buttonWrapper: {
		paddingTop: token('space.300'),
	},
});

export interface InsightsFeedErrorProps {
	/** Callback invoked when the "Try again" button is pressed */
	onRetry?: () => void;
	/** Test ID for targeting in tests */
	testId?: string;
}

/**
 * InsightsFeedError — A generic error screen displayed when insights fail to load.
 *
 * Shows an error icon, a heading, an explanatory message, and a prominent
 * "Try again" button that triggers the `onRetry` callback.
 */
export const InsightsFeedError = ({
	onRetry,
	testId = 'insights-feed-error',
}: InsightsFeedErrorProps): JSX.Element => (
	<Flex direction="column" xcss={styles.container} testId={testId}>
		<Box xcss={styles.iconWrapper}>
			<StatusErrorIcon label="" color={token('color.icon.danger')} size="medium" />
		</Box>
		<Stack space="space.100" alignInline="center">
			<Heading size="small">
				<FormattedMessage {...messages.errorTitle} />
			</Heading>
			<Text size="medium" color="color.text.subtle">
				<FormattedMessage {...messages.errorDescription} />
			</Text>
		</Stack>
		{onRetry && (
			<Box xcss={styles.buttonWrapper}>
				<Button appearance="primary" onClick={onRetry} testId={`${testId}__retry-button`}>
					<FormattedMessage {...messages.retryButton} />
				</Button>
			</Box>
		)}
	</Flex>
);
